import React, {lazy, ReactNode, Suspense} from 'react';
import {RouteObject} from 'react-router-dom';
import Layout from 'shared/components/Layout';
import {Dashboard, Job, Jobs} from 'components';
import {Favourites} from 'components/customer/Favourites/Favourites';
import {Cart} from 'shared/components/Header/Cart';
import {FourZeroFour} from 'shared/FourZeroFour';
import {JobRoute} from 'shared/auth/JobRoute';
import {Loader} from 'shared/helpers';
import {Confirmation} from 'components/customer/Confirmation';
import {JobSubmitSuccess} from 'components/customer/Confirmation/JobSubmitSuccess';
import {RoomPlannerProvider} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import {Payment} from 'components/customer/Confirmation/Payment';
import {Confirmation as PaymentConfirmation} from 'components/customer/Confirmation/Payment/Confirmation';
import {JobConfirmation} from 'components/customer/Confirmation/JobConfirmation';
import {MobileRestriction} from 'shared/components/MobileRestriction';
import {LayoutRedirect} from 'components/customer/LayoutDesigner/LayoutRedirect';

const LandingPage = lazy(() => import('components/customer/LandingPage'));
const Settings = lazy(() => import('components/customer/Settings/Settings'));
const Room = lazy(() => import('components/customer/Room/Room'));
const BenchtopModule = lazy(() => import('components/customer/BTM'));
const RoomPlannerDashboard = lazy(
    () =>
        import('components/customer/RoomPlanner/components/RoomPlannerDashboard')
);
const ProductWrapper = lazy(
    () => import('components/customer/Product/Products')
);
const KDMaxWrapper = lazy(
    () => import('components/customer/KDMax/KDMaxImport')
);
const QFPSwitch = lazy(() => import('components/customer/QFPRedux/QFPSwitch'));
const Hardware = lazy(() => import('components/customer/Hardware/Hardware'));
const Invoice = lazy(
    () => import('components/customer/Confirmation/Invoice/QuickView')
);
const Balance = lazy(() => import('components/customer/Confirmation/Balance'));

export const SuspenseLoader = ({children}: {children: ReactNode}) => (
    <Suspense fallback={<Loader loader={true} />}>{children}</Suspense>
);

export const routes: RouteObject[] = [
    {
        path: 'job/:jobId',
        element: <JobRoute checkProcessingStatus={true} />,
        children: [
            {
                path: '',
                element: (
                    <Layout withSidebar>
                        <Job />
                    </Layout>
                ),
            },
        ],
    },
    {
        path: 'job/:jobId/success',
        element: <JobRoute checkProcessingStatus={true} />,
        children: [
            {
                path: '',
                element: <JobSubmitSuccess />,
            },
        ],
    },
    {
        path: 'job/:jobId/confirmation',
        element: <JobRoute checkProcessingStatus={false} />,
        children: [
            {
                path: '',
                element: (
                    <Layout>
                        <Confirmation />
                    </Layout>
                ),
            },
        ],
    },
    {
        path: 'job/:jobId/payment',
        element: <JobRoute checkProcessingStatus={false} />,
        children: [
            {
                path: '',
                element: (
                    <Layout>
                        <Payment />
                    </Layout>
                ),
            },
        ],
    },
    {
        path: 'job/:jobId/payment-confirmation',
        element: <JobRoute checkProcessingStatus={false} />,
        children: [
            {
                path: '',
                element: (
                    <Layout>
                        <PaymentConfirmation />
                    </Layout>
                ),
            },
        ],
    },
    {
        path: 'job/:jobId/job-confirmation',
        element: <JobRoute checkProcessingStatus={false} />,
        children: [
            {
                path: '',
                element: (
                    <Layout>
                        <JobConfirmation />
                    </Layout>
                ),
            },
        ],
    },
    {
        path: 'job/:jobId/hardware',
        element: <JobRoute checkProcessingStatus={true} />,
        children: [
            {
                path: '',
                element: (
                    <Layout withSidebar>
                        <SuspenseLoader>
                            <Hardware />
                        </SuspenseLoader>
                    </Layout>
                ),
            },
        ],
    },
    {
        path: 'job/:jobId/room/:roomId',
        element: <JobRoute checkProcessingStatus={true} />,
        children: [
            {
                path: '',
                element: (
                    <Layout withSidebar>
                        <SuspenseLoader>
                            <Room />
                        </SuspenseLoader>
                    </Layout>
                ),
            },
        ],
    },
    {
        path: 'job/:jobId/room/:roomId/layout',
        element: <LayoutRedirect />,
    },
    {
        path: 'job/:jobId/room/:roomId/room-planner',
        element: <JobRoute checkProcessingStatus={true} />,
        children: [
            {
                path: '',
                element: (
                    <RoomPlannerProvider>
                        <Layout withSidebar>
                            <SuspenseLoader>
                                <MobileRestriction
                                    onlyShowMessage={false}
                                    message="Room planner works best on larger screens. Please maximize your window, reduce your browser zoom, or use a desktop, laptop or tablet.">
                                    <RoomPlannerDashboard />
                                </MobileRestriction>
                            </SuspenseLoader>
                        </Layout>
                    </RoomPlannerProvider>
                ),
            },
        ],
    },
    {
        path: 'job/:jobId/room/:roomId/product',
        element: <JobRoute checkProcessingStatus={true} />,
        children: [
            {
                path: '',
                element: (
                    <Layout withSidebar>
                        <SuspenseLoader>
                            <ProductWrapper />
                        </SuspenseLoader>
                    </Layout>
                ),
            },
        ],
    },
    {
        path: 'job/:jobId/room/:roomId/quick-flat-product',
        element: <JobRoute checkProcessingStatus={true} />,
        children: [
            {
                path: '',
                element: (
                    <Layout withSidebar>
                        <SuspenseLoader>
                            <QFPSwitch />
                        </SuspenseLoader>
                    </Layout>
                ),
            },
        ],
    },
    {
        path: 'job/:jobId/room/:roomId/kdmax-import',
        element: <JobRoute checkProcessingStatus={true} />,
        children: [
            {
                path: '',
                element: (
                    <Layout withSidebar>
                        <SuspenseLoader>
                            <KDMaxWrapper />
                        </SuspenseLoader>
                    </Layout>
                ),
            },
        ],
    },
    {
        path: 'job/:jobId/room/:roomId/dashboard',
        element: <JobRoute checkProcessingStatus={true} />,
        children: [
            {
                path: '',
                element: (
                    <Layout withSidebar>
                        <LandingPage />
                    </Layout>
                ),
            },
        ],
    },
    {
        path: 'job/:jobId/dashboard',
        element: <JobRoute checkProcessingStatus={true} />,
        children: [
            {
                path: '',
                element: (
                    <Layout withSidebar>
                        <LandingPage />
                    </Layout>
                ),
            },
        ],
    },
    {
        path: 'job/:jobId/room/:roomId/favourites',
        element: <JobRoute checkProcessingStatus={true} />,
        children: [
            {
                path: '',
                element: (
                    <Layout withSidebar>
                        <Favourites />
                    </Layout>
                ),
            },
        ],
    },
    {
        path: 'job/:jobId/cart',
        element: <JobRoute />,
        children: [
            {
                path: '',
                element: (
                    <Layout withSidebar>
                        <Cart />
                    </Layout>
                ),
            },
        ],
    },
    {
        path: 'job/:jobId/invoice',
        element: <JobRoute checkProcessingStatus={false} />,
        children: [
            {
                path: '',
                element: (
                    <Layout>
                        <Invoice />
                    </Layout>
                ),
            },
        ],
    },
    {
        path: 'job/:jobId/balance',
        element: <JobRoute />,
        children: [
            {
                path: '',
                element: (
                    <Layout>
                        <Balance />
                    </Layout>
                ),
            },
        ],
    },
    {
        path: 'job',
        element: <JobRoute />,
        children: [
            {
                path: '',
                element: (
                    <Layout>
                        <Job />
                    </Layout>
                ),
            },
        ],
    },
    {
        path: 'job/:jobId/room',
        element: <JobRoute checkProcessingStatus={true} />,
        children: [
            {
                path: '',
                element: (
                    <Layout withSidebar>
                        <SuspenseLoader>
                            <Room />
                        </SuspenseLoader>
                    </Layout>
                ),
            },
        ],
    },
    {
        path: 'job/:jobId/benchtop-module',
        element: <JobRoute checkProcessingStatus={true} />,
        children: [
            {
                path: '*',
                element: (
                    <Layout withSidebar>
                        <SuspenseLoader>
                            <BenchtopModule />
                        </SuspenseLoader>
                    </Layout>
                ),
            },
        ],
    },
    {
        path: 'jobs/:status',
        element: (
            <Layout>
                <Jobs />
            </Layout>
        ),
    },
    {
        path: 'jobs',
        element: (
            <Layout>
                <Jobs />
            </Layout>
        ),
    },
    {
        path: 'account-settings',
        children: [
            {
                path: '*',
                element: (
                    <Layout>
                        <SuspenseLoader>
                            <Settings />
                        </SuspenseLoader>
                    </Layout>
                ),
            },
        ],
    },
    {
        index: true,
        element: (
            <Layout>
                <Dashboard />
            </Layout>
        ),
    },
    {
        path: '*',
        element: (
            <Layout>
                <FourZeroFour />
            </Layout>
        ),
    },
];
